import { useState, useEffect } from 'react';
import {
  Table,
  Badge,
  Paper,
  Text,
  Group,
  Button,
  Modal,
  Stack,
  ScrollArea,
  Divider,
  Loader,
  Center,
  TextInput
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconMessageCircle, IconSend } from '@tabler/icons-react';
import supportService, { SupportTicket } from '../../features/support/supportService';

// Helper function for formatting dates
const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Status badge color mapping
const getStatusColor = (status: SupportTicket['status']) => {
  const colors: Record<SupportTicket['status'], string> = {
    'open': 'blue',
    'in-progress': 'yellow',
    'resolved': 'green',
    'closed': 'gray'
  };
  return colors[status];
};

// Priority badge color mapping
const getPriorityColor = (priority: SupportTicket['priority']) => {
  const colors: Record<SupportTicket['priority'], string> = {
    'low': 'blue',
    'medium': 'yellow',
    'high': 'red'
  };
  return colors[priority];
};

const SupportTicketList = () => {
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [opened, { open, close }] = useDisclosure(false);

  const replyForm = useForm({
    initialValues: {
      message: ''
    },
    validate: {
      message: (value) => value.trim().length < 5 ? 'Message is too short' : null
    }
  });

  useEffect(() => {
    const fetchTickets = async () => {
      try {
        const data = await supportService.getTickets();
        setTickets(data);
      } catch (error) {
        notifications.show({
          title: 'Error',
          message: 'Failed to load support tickets',
          color: 'red'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchTickets();
  }, []);

  const handleViewTicket = (ticket: SupportTicket) => {
    setSelectedTicket(ticket);
    open();
  };

  const handleSubmitReply = async (values: { message: string }) => {
    if (!selectedTicket) return;

    try {
      const updatedTicket = await supportService.addReply(selectedTicket.id, values.message);
      if (updatedTicket) {
        setSelectedTicket(updatedTicket);

        // Update the ticket in the list
        setTickets(prevTickets =>
          prevTickets.map(t => t.id === updatedTicket.id ? updatedTicket : t)
        );

        notifications.show({
          title: 'Reply Sent',
          message: 'Your reply has been sent successfully',
          color: 'green'
        });

        replyForm.reset();
      }
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to send your reply',
        color: 'red'
      });
    }
  };

  return (
    <Paper withBorder p={0}>
      {loading ? (
        <Center p="xl">
          <Loader size="md" />
        </Center>
      ) : tickets.length === 0 ? (
        <Center p="xl">
          <Text c="dimmed">No support tickets found. Create a new ticket in the Help & Support section.</Text>
        </Center>
      ) : (
        <ScrollArea>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>ID</Table.Th>
                <Table.Th>Subject</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Priority</Table.Th>
                <Table.Th>Created</Table.Th>
                <Table.Th>Last Updated</Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {tickets.map((ticket) => (
                <Table.Tr key={ticket.id}>
                  <Table.Td>{ticket.id}</Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      {ticket.messages.length > 1 && (
                        <IconMessageCircle size="1rem" color="gray" />
                      )}
                      <Text>{ticket.subject}</Text>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Badge color={getStatusColor(ticket.status)}>
                      {ticket.status.replace('-', ' ')}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Badge color={getPriorityColor(ticket.priority)}>
                      {ticket.priority}
                    </Badge>
                  </Table.Td>
                  <Table.Td>{formatDate(ticket.createdAt)}</Table.Td>
                  <Table.Td>{formatDate(ticket.lastUpdated)}</Table.Td>
                  <Table.Td>
                    <Button
                      variant="light"
                      onClick={() => handleViewTicket(ticket)}
                    >
                      View
                    </Button>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      )}

      {/* Ticket Detail Modal */}
      <Modal
        opened={opened}
        onClose={close}
        title={`Ticket ${selectedTicket?.id}: ${selectedTicket?.subject}`}
        size="lg"
      >
        {selectedTicket && (
          <Stack>
            <Group grow>
              <Paper withBorder p="xs">
                <Text size="sm" fw={500}>Status</Text>
                <Badge color={getStatusColor(selectedTicket.status)}>
                  {selectedTicket.status.replace('-', ' ')}
                </Badge>
              </Paper>
              <Paper withBorder p="xs">
                <Text size="sm" fw={500}>Priority</Text>
                <Badge color={getPriorityColor(selectedTicket.priority)}>
                  {selectedTicket.priority}
                </Badge>
              </Paper>
              <Paper withBorder p="xs">
                <Text size="sm" fw={500}>Category</Text>
                <Text size="sm">{selectedTicket.category}</Text>
              </Paper>
            </Group>

            <Divider label="Conversation" labelPosition="center" />

            <ScrollArea h={300}>
              <Stack>
                {selectedTicket.messages.map((message, index) => (
                  <Paper
                    key={index}
                    p="sm"
                    withBorder
                    bg={message.sender === 'support' ? 'var(--mantine-color-blue-0)' : 'white'}
                  >
                    <Group justify="space-between" mb="xs">
                      <Text fw={500}>
                        {message.sender === 'user' ? 'You' : 'Support Agent'}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {formatDate(message.timestamp)}
                      </Text>
                    </Group>
                    <Text size="sm">{message.message}</Text>
                  </Paper>
                ))}
              </Stack>
            </ScrollArea>

            {selectedTicket.status !== 'closed' && (
              <>
                <Divider label="Reply" labelPosition="center" />
                <form onSubmit={replyForm.onSubmit(handleSubmitReply)}>
                  <TextInput
                    placeholder="Type your reply here..."
                    rightSection={
                      <Button type="submit" variant="subtle" >
                        <IconSend size="1rem" />
                      </Button>
                    }
                    {...replyForm.getInputProps('message')}
                  />
                </form>
              </>
            )}
          </Stack>
        )}
      </Modal>
    </Paper>
  );
};

export default SupportTicketList;
